/**
 * @file ref-key-input.tsx
 * @module app/modules/service-request/components/fields/ref-key-input
 * @description Token input for order reference keys. Typing a separator, pressing Enter, pasting
 * a list, or leaving the field turns text into chips; duplicates and keys past the limit are skipped.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 2.1.2
 * @version 2.1.2
 */

import { ClipboardEvent, KeyboardEvent, useRef, useState } from 'react';
import { X } from 'lucide-react';
import {
  mergeRefKeys,
  REF_KEY_SEPARATOR,
  splitRefKeys,
} from '../../utils/ref-keys';

type RefKeyInputProps = {
  id: string;
  value: string[];
  onChange: (refKeys: string[]) => void;
  limit: number;
  invalid?: boolean;
  disabled?: boolean;
  describedBy?: string;
};

const RefKeyInput = ({
  id,
  value,
  onChange,
  limit,
  invalid,
  disabled,
  describedBy,
}: RefKeyInputProps) => {
  const [draft, setDraft] = useState('');
  const [notice, setNotice] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const commit = (raw: string) => {
    const incoming = splitRefKeys(raw);
    if (!incoming.length) return;
    const { refKeys, duplicates, overLimit } = mergeRefKeys(
      value,
      incoming,
      limit
    );
    const skipped = [
      duplicates
        ? `${duplicates} duplicate${duplicates > 1 ? 's' : ''} skipped`
        : '',
      overLimit ? `${overLimit} over the ${limit}-order limit skipped` : '',
    ].filter(Boolean);
    setNotice(skipped.join(' · '));
    if (refKeys.length !== value.length) onChange(refKeys);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if ((event.key === 'Enter' || event.key === 'Tab') && draft.trim()) {
      event.preventDefault();
      commit(draft);
      setDraft('');
      return;
    }
    if (event.key === 'Backspace' && !draft && value.length) {
      onChange(value.slice(0, -1));
      setNotice('');
    }
  };

  const handleChange = (text: string) => {
    if (REF_KEY_SEPARATOR.test(text)) {
      const parts = text.split(REF_KEY_SEPARATOR);
      const trailing = parts.pop() || '';
      commit(parts.join(','));
      setDraft(trailing);
      return;
    }
    setDraft(text);
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData.getData('text');
    if (!REF_KEY_SEPARATOR.test(text)) return;
    event.preventDefault();
    commit(`${draft},${text}`);
    setDraft('');
  };

  const remove = (refKey: string) => {
    onChange(value.filter((item) => item !== refKey));
    setNotice('');
    inputRef.current?.focus();
  };

  return (
    <div>
      <div
        className={`sr-token-input${invalid ? ' is-invalid' : ''}${
          disabled ? ' is-disabled' : ''
        }`}
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((refKey) => (
          <span key={refKey} className="sr-chip">
            {refKey}
            <button
              type="button"
              className="sr-chip__remove"
              aria-label={`Remove ${refKey}`}
              disabled={disabled}
              onClick={(event) => {
                event.stopPropagation();
                remove(refKey);
              }}
            >
              <X size={12} aria-hidden="true" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          id={id}
          className="sr-token-input__field"
          value={draft}
          disabled={disabled || value.length >= limit}
          placeholder={
            value.length ? '' : 'Type or paste refKeys, e.g. OR0001, OR0002'
          }
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          autoComplete="off"
          spellCheck={false}
          onChange={(event) => handleChange(event.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={() => {
            commit(draft);
            setDraft('');
          }}
        />
      </div>
      <div className="sr-field-hint" aria-live="polite">
        <span>
          {value.length} of {limit} orders
          {notice ? (
            <span className="sr-field-hint__notice"> · {notice}</span>
          ) : null}
        </span>
        {value.length > 0 && !disabled ? (
          <button
            type="button"
            className="btn btn-link btn-sm p-0 sr-link"
            onClick={() => {
              onChange([]);
              setNotice('');
            }}
          >
            Clear all
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default RefKeyInput;
